import { ChoiceGroup, IChoiceGroupOption, IChoiceGroupStyles, PrimaryButton, Spinner, SpinnerSize, Stack } from "@fluentui/react";
import React from "react";
import { LanguageContext } from "../../../../../../utilities/LocalizationModule";
import { CheckinTypes, ConvertAndSaveAsPDFRequest } from "../../redux/api/local/api-local-interfaces";
import { OneDriveOverWriteEnum } from "../../../enums/oneDriveOverWriteEnum";
import { VersionSelectorMinMajOnly } from "./VersionSelectorMinMajOnly";
import { ComponentTypes } from "../../../../config/config";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/storeMain";
import authModule from "../../../../../../utilities/AuthModule";
import { logger } from "../../../../../../utilities/logger";
import { changeExtensionToPdf } from "../../../helpers/stringHelper";
import { apiFileConvertAndSaveAsPDF } from "../../../../services/ApiService";
import { IOneDriveRootDetails } from "../../../../interfaces/IOneDriveRootDetails";
import { getOneDriveRootDetails } from "../../../../services/GraphService";

const choiceGroupStyles: IChoiceGroupStyles = { label: { fontSize: 16 } };

export interface IDuplicatePDFFileVersionSelectorProps {
  showSelectVersion: boolean;
  isDuplicate: boolean;
  selectedItem: any;
  onCancel: () => void;
  onSuccess: () => void;
}

export const DuplicatePDFFileVersionSelector = ({ showSelectVersion, isDuplicate, selectedItem, onCancel, onSuccess }: IDuplicatePDFFileVersionSelectorProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
  const tenantId = useSelector((state: RootState) => state.user?.account?.tenantId);
  const accountId = useSelector((state: RootState) => state.user?.account?.accountId);

  const [selectedOverwriteOption, setSelectedOverwriteOption] = React.useState(OneDriveOverWriteEnum.rename.toString());
  const [selectedCheckinType, setSelectedCheckinType] = React.useState<CheckinTypes>(CheckinTypes.minorCheckIn);

  const [isConverting, setIsConverting] = React.useState(false);

  const [overwriteOptions, setOverwriteOptions] = React.useState<IChoiceGroupOption[]>([
    { key: OneDriveOverWriteEnum.rename.toString(), text: languageStrings.Rename },
    { key: OneDriveOverWriteEnum.replace.toString(), text: languageStrings.Replace }
  ])

  const saveAndConvertAsPdfFile = async () => {
    setIsConverting(true);
    let isInOneDrive = currentDirectory.type == ComponentTypes.OneDrive || currentDirectory.type == ComponentTypes.OneDriveSubFolder || currentDirectory.webUrl?.includes("sharepoint.com/personal/");

    let driveId: string;
    let siteUrl: string;
    let sharePointToken: string;

    if (isInOneDrive) {
      let oneDriveDetails: IOneDriveRootDetails = oneDriveRootDetails;
      if (!oneDriveDetails) {
        oneDriveDetails = await getOneDriveRootDetails();
      }

      driveId = oneDriveDetails.driveId;
      siteUrl = oneDriveDetails.sharePointSiteUrl;
      sharePointToken = await authModule.getOneDriveAccessToken();
    }
    else {
      driveId = !currentDirectory.parentId ? currentDirectory.id : currentDirectory.parentId;
      siteUrl = currentDirectory.webUrl;
      sharePointToken = await authModule.getSharePointAccessToken();
    }

    const spItemUrlparts = selectedItem['.spItemUrl'].split('/');
    let itemId: string = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];

    const request: ConvertAndSaveAsPDFRequest = {
      graphToken: await authModule.getGraphAccessToken(),
      sharePointToken,
      tenantGuid: tenantId,
      accountId,
      driveId,
      itemId,
      siteUrl,
      listId: currentDirectory.sharePointListId,
      teamId: workingDirectories?.find(dir => dir.teamGroupId)?.teamGroupId,
      channelId: workingDirectories?.find(dir => dir.teamChannelId)?.teamChannelId,
      fileUniqueId: selectedItem?.UniqueId.replace("{", "").replace("}", ""),
      EnsureUniqueFilename: selectedOverwriteOption === OneDriveOverWriteEnum.rename.toString() ? true : false,
      checkinType: selectedCheckinType,
    }
    try {
      await apiFileConvertAndSaveAsPDF(request);
      setIsConverting(false);
      onSuccess();
    } catch (error) {
      logger(error, "FileOptionsPanel.tsx (saveAsPdfFile)");
      setIsConverting(false);
      onCancel();
    }
  }

  return (
    <div>
      {isDuplicate && <div style={{ marginBottom: showSelectVersion ? 15 : 0 }}>
        <ChoiceGroup
          defaultSelectedKey={OneDriveOverWriteEnum.rename.toString()}
          options={overwriteOptions}
          onChange={(ev: React.FormEvent<HTMLInputElement>, option: IChoiceGroupOption) => {
            setSelectedOverwriteOption(option.key);
          }}
          label={`${changeExtensionToPdf(selectedItem?.FileLeafRef)} ${languageStrings.AlreadyExists.toLowerCase()}`}
          styles={choiceGroupStyles}
          required={true}
        />
        {!showSelectVersion &&
          <div style={{ paddingTop: 20 }}>
            <Stack tokens={{ childrenGap: 10 }} horizontal>
              <PrimaryButton onClick={() => { saveAndConvertAsPdfFile(); }} text={languageStrings.Save} disabled={isConverting}>{isConverting && <Spinner size={SpinnerSize.small} style={{ paddingLeft: '5px' }} />}</PrimaryButton>
              <PrimaryButton text={languageStrings.Cancel} allowDisabledFocus onClick={() => { onCancel() }} disabled={isConverting} />
            </Stack>
          </div>
        }
      </div>}
      {showSelectVersion && <VersionSelectorMinMajOnly
        checkinTypeChanged={setSelectedCheckinType}
        onCancel={() => {
          onCancel();
        }}
        onSave={() => {
          saveAndConvertAsPdfFile();
        }}
        isBusy={isConverting}
        labelFontSize={16}
        paddingTop={0}
        paddingLeft={0} />}
    </div>
  );
};
